import type { LearningDevice } from 'definitions';
import type { LearningsPageResponse } from '@@/api/learnings/page/page-types';
import type { MapFilter, MapRow } from '@/state/learnings/page-view';
import { useState } from 'react';
import { cn } from 'helpers/cn';
import {
  axisTicks,
  barGeometry,
  filterMapRows,
  mapRows,
  scaleFor,
  signedPercent,
  signedRange,
  TONE_TEXT,
} from '@/state/learnings/page-view';
import { EmptyState, FOCUS_RING, PillTabs, TableScroll } from '../../ConsolePanel';
import { Axis, IntervalBar, Section } from './parts';

const MAP_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Pages', value: 'pages' },
  { label: 'Elements', value: 'elements' },
  { label: 'Mechanisms', value: 'mechanisms' },
];

const COLUMNS = 'minmax(180px,1.2fr) minmax(220px,2fr) 60px 44px';

type DeviceFilterProps = {
  devices: readonly LearningDevice[];
  device: LearningDevice | null;
  onChange: (device: LearningDevice | null) => void;
};

/** All devices, or one: shown only when some test was aimed at a device. */
export const DeviceFilter = ({ devices, device, onChange }: DeviceFilterProps) =>
  devices.length === 0 ? null : (
    <PillTabs
      options={[{ label: 'All devices', value: '' }, ...devices.map((entry) => ({ label: entry, value: entry }))]}
      value={device ?? ''}
      onChange={(value) => onChange(value ? (value as LearningDevice) : null)}
    />
  );

type EvidenceMapProps = {
  page: LearningsPageResponse;
  device: LearningDevice | null;
  onDevice: (device: LearningDevice | null) => void;
  onOpenPattern: (patternKey: string) => void;
  onOpenTest: (experimentId: number) => void;
};

/**
 * Every pattern on one axis, merged where patterns hold the same tests, with the adjusted range as
 * the band. A row opens its pattern; a surprise row opens its test.
 */
export const EvidenceMap = ({ page, device, onDevice, onOpenPattern, onOpenTest }: EvidenceMapProps) => {
  const [filter, setFilter] = useState<MapFilter>('all');
  const rows = filterMapRows(
    mapRows({ evidence: page.evidence, labels: page.labels, surprises: page.surprises }),
    filter,
  );

  return (
    <Section
      title="Evidence map"
      description="Every pattern on one axis. Rows that share the same tests are merged."
      tourAnchor="learnings-map"
      actions={
        <>
          <DeviceFilter devices={page.devices} device={device} onChange={onDevice} />
          <PillTabs options={MAP_OPTIONS} value={filter} onChange={(value) => setFilter(value as MapFilter)} />
        </>
      }
    >
      <PatternBars rows={rows} onOpenPattern={onOpenPattern} onOpenTest={onOpenTest} />
      <div className="flex flex-wrap items-center gap-4 px-3.5 py-2.5">
        <Legend className="bg-ok">Leaning positive</Legend>
        <Legend className="bg-danger">Leaning negative</Legend>
        <Legend className="bg-bar-muted">Mixed</Legend>
        <span className="supporting">Bands are multiplicity-adjusted, so they run wide.</span>
      </div>
    </Section>
  );
};

type PatternBarsProps = {
  rows: readonly MapRow[];
  onOpenPattern: (patternKey: string) => void;
  onOpenTest: (experimentId: number) => void;
};

/** Rows of patterns on one shared axis: a name and kind, the band, the lift and the tests. */
export const PatternBars = ({ rows, onOpenPattern, onOpenTest }: PatternBarsProps) => {
  const scale = scaleFor(rows.flatMap((row) => [row.mean, row.lower ?? row.mean, row.upper ?? row.mean]));
  const open = (row: MapRow) =>
    row.grouping === 'surprise' ? onOpenTest(Number(row.key.split(':')[1])) : onOpenPattern(row.key);

  return (
    <div className="px-3.5">
      <TableScroll minWidth={520}>
        <div className="border-line grid items-center gap-3.5 border-b py-2" style={{ gridTemplateColumns: COLUMNS }}>
          <span className="micro-label">Pattern</span>
          <Axis ticks={axisTicks(scale)} />
          <span className="micro-label text-right">Lift</span>
          <span className="micro-label text-right">Tests</span>
        </div>
        {rows.map((row) => (
          <button
            key={row.key}
            type="button"
            onClick={() => open(row)}
            className={cn(
              'border-line hover:bg-hover grid w-full items-center gap-3.5 border-b py-2 text-left transition-colors duration-150',
              FOCUS_RING,
            )}
            style={{ gridTemplateColumns: COLUMNS }}
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-meta">{row.label}</span>
              <span className={cn('micro-label', row.grouping === 'surprise' && 'text-accent')}>{row.kind}</span>
            </span>
            <IntervalBar
              bar={barGeometry({ mean: row.mean, lower: row.lower, upper: row.upper, scale })}
              tone={row.tone}
              label={`${signedPercent(row.mean)}${row.lower !== null && row.upper !== null ? `, range ${signedRange(row.lower, row.upper)}` : ''}`}
            />
            <span className={cn('num text-right font-medium', TONE_TEXT[row.tone])}>{signedPercent(row.mean)}</span>
            <span className="num text-dim text-right">{row.tests}</span>
          </button>
        ))}
      </TableScroll>
      {rows.length === 0 && (
        <div className="py-3.5">
          <EmptyState>No pattern of this kind has a reading yet.</EmptyState>
        </div>
      )}
    </div>
  );
};

type LegendProps = { className: string; children: string };

const Legend = ({ className, children }: LegendProps) => (
  <span className="supporting inline-flex items-center gap-1.5">
    <span className={cn('h-1 w-3 rounded-pill', className)} />
    {children}
  </span>
);
